import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PolicyKey } from '@kirin/api-client/contracts/policies';
import {
  PolicyExceptionAction,
  SKILL_HASH_SCHEMES,
  SKILL_VERSION_RULE_FIELDS,
  skillIdentityName,
  type PolicyException,
  type SkillVersion,
} from '@kirin/api-client/contracts/policy-exceptions';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import { listOrganizationPolicyExceptions } from '@kirin/api-client/api/policy-exceptions';
import { allowSkillVersion } from '@kirin/api-client/api/skills';
import { ApiError } from '@kirin/api-client/errors';
import { useCreateOrganizationPolicyException } from './useOrganizationPolicyExceptions';
import { queryKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-errors';
import { trackEvent } from '@/lib/analytics';

/**
 * What identifies a listed skill. The triple (KIR-4849) is required:
 * `(name, source)` alone matched the same-named skill in every repository,
 * so one mark deleted them all. `version` (KIR-5174) is which copy of the
 * skill this is: two same-named skills whose SKILL.md differs are two rows,
 * and a mark on one must leave the other alone.
 */
export interface SkillIdentity {
  name: string;
  source: string;
  /** Normalized git origin, or null for user/plugin and origin-less skills. */
  project: string | null;
  /** The row's version, or null while it is not known yet. */
  version: SkillVersion | null;
  /**
   * What the admin sees. Toasts use this; the rule still stores the folded
   * `name`, because the matcher folds both sides.
   */
  displayName?: string;
}

/** Which way a row was moved, for analytics. */
type SkillListChange = 'mark_for_delete' | 'allow';

/**
 * Skills have no Allow list — the backend refuses `action: 'allow'` — so the
 * only states are marked-for-delete and not marked. "Allow" means deleting
 * the Block rule.
 *
 * Every action resolves, never rejects: a failure is reported with a toast
 * inside the hook, so a caller's `void actions.allow(...)` cannot produce an
 * unhandled rejection.
 */
export interface SkillListActions {
  /** Put the skill on the marked-for-delete list. */
  markForDelete: (skill: SkillIdentity) => Promise<void>;
  /** Remove the skill's rule, returning it to allowed. */
  allow: (skill: SkillIdentity) => Promise<void>;
  /**
   * True for this skill from the click until the Skills table has refetched —
   * not merely until the mutation settles. In between, the row still shows
   * the old state and the rule list may not include the new rule yet, so a
   * second click would act on stale data and send a duplicate create. Other
   * rows stay usable meanwhile.
   */
  isPending: (skill: SkillIdentity) => boolean;
}

/** `project` is compared null-sensitively; a blank string means none. */
function normalizeProject(project: string | null | undefined): string | null {
  return project?.trim() || null;
}

/** Hashes are lowercase hex; a blank one means none. */
function normalizeHash(hash: unknown): string | null {
  return typeof hash === 'string' ? hash.trim().toLowerCase() || null : null;
}

/** A version as one comparable string, or '' for none. */
function versionKey(version: SkillVersion | null): string {
  const hash = normalizeHash(version?.hash);
  return version && hash ? `${version.scheme}:${hash}` : '';
}

/**
 * The version a stored rule names, under whichever scheme's field it uses,
 * or null when it names none: the backend's `readSkillRuleVersion`.
 */
function ruleVersion(ruleObject: Record<string, unknown>): SkillVersion | null {
  for (const scheme of SKILL_HASH_SCHEMES) {
    const hash = normalizeHash(ruleObject[SKILL_VERSION_RULE_FIELDS[scheme]]);
    if (hash !== null) return { scheme, hash };
  }
  return null;
}

/** Identity key for the in-flight set, using the same comparison as {@link rulesFor}. */
function skillKey(skill: SkillIdentity): string {
  return [
    skillIdentityName(skill.name),
    skill.source.trim().toLowerCase(),
    normalizeProject(skill.project) ?? '',
    versionKey(skill.version),
  ].join('\u0000');
}

/**
 * The stored rules this row is marked by.
 *
 * Mirrors `loadMarkedSkillRules` (backend `domain/handlers/skills/list-skills.ts`),
 * NOT the scanner's `matchesRuleForSkills` — deliberately, and the difference
 * matters. `loadMarkedSkillRules` is what computes the `listState` the row
 * renders, and it is the looser of the two: it ignores unknown keys and reads
 * an absent or blank `project` as null. The scanner's matcher rejects both.
 *
 * A rule naming a version (KIR-5174) marks only the row with that `version`.
 * A rule naming none marks every version of the triple.
 *
 * Matching the stricter one here would mean a row the server shows as
 * "Marked for delete" could have no rule this function recognises, so Allow
 * would delete nothing and silently leave the row marked.
 */
function rulesFor(
  exceptions: PolicyException[] | undefined,
  skill: SkillIdentity
): PolicyException[] {
  const name = skillIdentityName(skill.name);
  const source = skill.source.trim().toLowerCase();
  const project = normalizeProject(skill.project);

  return (exceptions ?? []).filter(exception => {
    const {
      name: ruleName,
      source: ruleSource,
      project: ruleProject,
    } = exception.ruleObject;
    const ruleNames = ruleVersion(exception.ruleObject);
    if (typeof ruleName !== 'string' || typeof ruleSource !== 'string') {
      return false;
    }
    // Anything that is not a non-blank string reads as "no project", exactly
    // as the backend reader does.
    const normalizedRuleProject =
      typeof ruleProject === 'string' ? normalizeProject(ruleProject) : null;

    return (
      ruleSource.trim().toLowerCase() === source &&
      normalizedRuleProject === project &&
      skillIdentityName(ruleName) === name &&
      (ruleNames === null ||
        versionKey(ruleNames) === versionKey(skill.version))
    );
  });
}

/**
 * Mark / allow actions for a skill row in Inventory → Skills (KIR-4699).
 *
 * Organization scope only: there is no account-scoped skills route, so the
 * tab — and therefore this hook — only ever runs inside an organization.
 *
 * The rule list is fetched fresh inside each action and used ONLY to resolve
 * the exception ids it needs, never to render a row's state. A cached copy
 * can lag another admin's change or this user's last click. State comes from
 * the server-computed `listState` on the row, because that is what the pill
 * filter and pagination are built from, and two independently derived answers
 * on the same screen would eventually disagree in front of a user.
 *
 * A successful change emits `skill_list_change`.
 */
export function useSkillListActions(
  organizationId: OrganizationId | undefined
): SkillListActions {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const policyKey = PolicyKey.SkillsManagement;
  // The ref is the real guard: it updates synchronously, so two clicks on the
  // same row in the same frame cannot both pass. The state mirrors it for
  // rendering.
  const inFlight = useRef(new Set<string>());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  const fetchMarkedRules = useCallback((): Promise<PolicyException[]> => {
    const action = PolicyExceptionAction.Block;
    if (organizationId == null) return Promise.resolve([]);
    return queryClient.fetchQuery({
      queryKey: queryKeys.policyExceptions.organization(
        organizationId,
        policyKey,
        action
      ),
      queryFn: async () =>
        (
          await listOrganizationPolicyExceptions(
            apiClient,
            organizationId,
            policyKey,
            action
          )
        ).exceptions,
      staleTime: 0,
    });
  }, [queryClient, organizationId, policyKey]);

  const create = useCreateOrganizationPolicyException(
    organizationId,
    policyKey
  );
  // `useMutation` returns a new object every render; only `mutateAsync` is
  // stable. Depending on the whole object would rebuild every action, and so
  // the Skills columns, on each render — and the DataTable re-derives its URL
  // state from its columns, which loops.
  const createRule = create.mutateAsync;

  /**
   * Refetch the Skills table and resolve once it has landed. The mutation
   * hooks invalidate the rule lists too, but without awaiting; this one is
   * awaited so {@link SkillListActions.isPending} covers the refetch window.
   */
  const refreshSkills = useCallback(
    (): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: queryKeys.skills.all }),
    [queryClient]
  );

  const run = useCallback(
    async (
      skill: SkillIdentity,
      change: SkillListChange,
      failureTitle: string,
      /** Resolves `true` only when it actually wrote; see {@link trackEvent}. */
      work: () => Promise<boolean>
    ): Promise<void> => {
      const key = skillKey(skill);
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      setBusy(new Set(inFlight.current));
      try {
        // Only a real write is a list change. Both actions are no-ops when
        // the row already holds the requested state, and counting those would
        // inflate the reversal-rate metric this event feeds.
        if (!(await work())) return;
        trackEvent('skill_list_change', {
          scope: 'organization',
          source: 'skills_page',
          change,
          // Bucketed: a plugin name is user-controlled free text, so sending
          // it raw would both explode property cardinality and ship a
          // customer-identifying string. No skill name, no repo origin.
          skill_source: skill.source.startsWith('plugin:')
            ? 'plugin'
            : skill.source,
        });
      } catch (error) {
        toast({
          title: failureTitle,
          description: getApiErrorMessage(error, 'Please try again.'),
          variant: 'destructive',
        });
      } finally {
        // Refetch on failure too: another admin may have changed the row.
        await refreshSkills().catch(() => undefined);
        inFlight.current.delete(key);
        setBusy(new Set(inFlight.current));
      }
    },
    [toast, refreshSkills]
  );

  const markForDelete = useCallback(
    (skill: SkillIdentity) =>
      run(
        skill,
        'mark_for_delete',
        `Could not mark ${skill.displayName ?? skill.name} for delete`,
        async () => {
          // Already marked: nothing to create. The API would answer 409 rather
          // than be a no-op, which would surface as a failed action for
          // something the admin's intent already holds true for.
          if (rulesFor(await fetchMarkedRules(), skill).length > 0)
            return false;
          // A rule naming no version would mark every same-named skill, the
          // bug KIR-5174 fixes. Refuse rather than over-mark.
          const hash = normalizeHash(skill.version?.hash);
          if (skill.version === null || hash === null) {
            throw new Error(
              'Its SKILL.md has not been reported yet. Try again after the next scan.'
            );
          }
          try {
            await createRule({
              action: PolicyExceptionAction.Block,
              // The FOLDED name, never the row's `displayName` — the matcher
              // folds both sides, and a rule is stored verbatim.
              ruleObject: {
                name: skillIdentityName(skill.name),
                source: skill.source,
                project: normalizeProject(skill.project),
                [SKILL_VERSION_RULE_FIELDS[skill.version.scheme]]: hash,
              },
            });
          } catch (error) {
            // A 409 means someone else got there first: the mark holds.
            if (!(error instanceof ApiError && error.status === 409)) {
              throw error;
            }
            // Their write is the one that changed the list, not ours.
            return false;
          }
          return true;
        }
      ),
    [run, fetchMarkedRules, createRule]
  );

  const allow = useCallback(
    (skill: SkillIdentity) =>
      run(
        skill,
        'allow',
        `Could not allow ${skill.displayName ?? skill.name}`,
        async () => {
          if (organizationId == null) return false;
          // Nothing marks this row: nothing to remove, and nothing to count.
          if (rulesFor(await fetchMarkedRules(), skill).length === 0) {
            return false;
          }
          // One server call rather than a delete per rule: a rule naming no
          // version also marks this skill's OTHER versions, and replacing it
          // with per-version rules must happen in one transaction, or the
          // agent sees those versions unmarked in between (KIR-5174).
          const { removed } = await allowSkillVersion(
            apiClient,
            organizationId,
            {
              name: skillIdentityName(skill.name),
              source: skill.source,
              project: normalizeProject(skill.project),
              version: skill.version
                ? {
                    scheme: skill.version.scheme,
                    hash: skill.version.hash.trim().toLowerCase(),
                  }
                : null,
            }
          );
          // The rule lists other screens show changed too.
          void queryClient.invalidateQueries({
            queryKey: [
              'policyExceptions',
              'organization',
              organizationId,
              policyKey,
            ],
          });
          // Every rule was already gone: another admin got there first, and
          // counting this click would inflate the reversal-rate metric.
          // Mirrors the 409 arm of `markForDelete`.
          return removed > 0;
        }
      ),
    [run, fetchMarkedRules, organizationId, queryClient, policyKey]
  );

  const isPending = useCallback(
    (skill: SkillIdentity) => busy.has(skillKey(skill)),
    [busy]
  );

  return useMemo<SkillListActions>(
    () => ({ markForDelete, allow, isPending }),
    [markForDelete, allow, isPending]
  );
}
